/**
 * Spectator Page - Browse and watch live matches
 */

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { spectatorApi, LiveMatch } from '../../api/spectator';
import { useSpectatorStore } from '../../state/spectatorStore';
import { SpectatorBetPanel } from '../components/SpectatorBetPanel';
import { formatLamports } from '../../utils/format';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';

export const SpectatorPage: React.FC = () => {
  const [liveMatches, setLiveMatches] = useState<LiveMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    connected,
    isWatching,
    watchingMatchId,
    matchState,
    connect,
    disconnect,
    watchMatch,
    stopWatching,
  } = useSpectatorStore();

  // Fetch live matches
  const fetchMatches = useCallback(async () => {
    try {
      const matches = await spectatorApi.getLiveMatches();
      setLiveMatches(matches);
      setError(null);
    } catch (err) {
      setError('Failed to fetch live matches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 10000);
    return () => clearInterval(interval);
  }, [fetchMatches]);

  // Connect to spectator room on mount
  useEffect(() => {
    if (!connected) {
      connect(WS_URL);
    }
    return () => disconnect();
  }, []);

  const handleWatch = (matchId: string) => {
    watchMatch(matchId);
  };

  const handleStopWatching = () => {
    stopWatching();
  };

  // Watching view
  if (isWatching && matchState) {
    const players = Array.from(matchState.players.values());
    const player1 = players[0];
    const player2 = players[1];

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 p-4">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={handleStopWatching}
              className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/20"
            >
              <span>Back to Matches</span>
            </button>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
              <span className="text-sm text-red-400">LIVE (5s delay)</span>
            </div>
          </div>

          {/* Match info */}
          <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Match #{watchingMatchId?.slice(0, 8)}</span>
              <span className="text-sm text-white/50">
                Turn {matchState.currentTurn}/{matchState.maxTurns}
              </span>
              <span className="rounded-full bg-neon-500/20 px-3 py-1 text-xs text-neon-300">
                {matchState.wagerAmount > 0
                  ? formatLamports(
                      BigInt(matchState.wagerAmount),
                      matchState.currency === 'USDC' ? 'USDC' : 'SOL',
                    )
                  : 'Practice'}
              </span>
            </div>
          </div>

          {/* Players */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Player 1 */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="font-semibold text-blue-300">
                  {player1?.username || 'Player 1'}
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <span
                      key={i}
                      className={`text-lg ${i < (player1?.hearts || 0) ? 'text-red-500' : 'text-white/20'}`}
                    >
                      &hearts;
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <span className="text-3xl sm:text-4xl font-bold text-white">
                  {player1?.score || 0}
                </span>
                <span className="ml-2 text-white/50">pts</span>
              </div>
              {player1?.jokerIds && player1.jokerIds.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1">
                  {player1.jokerIds.map((jokerId) => (
                    <span
                      key={jokerId}
                      className="rounded bg-white/10 px-2 py-0.5 text-[10px] text-white/60"
                    >
                      {jokerId}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Player 2 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="font-semibold text-purple-300">
                  {player2?.username || 'Player 2'}
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <span
                      key={i}
                      className={`text-lg ${i < (player2?.hearts || 0) ? 'text-red-500' : 'text-white/20'}`}
                    >
                      &hearts;
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <span className="text-3xl sm:text-4xl font-bold text-white">
                  {player2?.score || 0}
                </span>
                <span className="ml-2 text-white/50">pts</span>
              </div>
              {player2?.jokerIds && player2.jokerIds.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1">
                  {player2.jokerIds.map((jokerId) => (
                    <span
                      key={jokerId}
                      className="rounded bg-white/10 px-2 py-0.5 text-[10px] text-white/60"
                    >
                      {jokerId}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Turn history */}
          {matchState.turnResults.length > 0 && (
            <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4">
              <h3 className="mb-3 text-sm font-medium text-white/70">Turn History</h3>
              <div className="space-y-2">
                {matchState.turnResults.map((result, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm"
                  >
                    <span className="text-white/50">Turn {result.turn}</span>
                    <span className="text-white/70">{result.wallet.slice(0, 8)}...</span>
                    <span className="text-neon-300">{result.handType}</span>
                    <span className="font-semibold text-white">+{result.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phase indicator */}
          <div className="mt-6 text-center">
            <span className="rounded-full bg-amber-500/20 px-4 py-2 text-sm font-medium text-amber-300">
              Phase: {matchState.phase}
            </span>
          </div>

          {/* Spectator Betting */}
          {watchingMatchId && players.length >= 2 && (
            <div className="mt-6">
              <SpectatorBetPanel
                matchId={watchingMatchId}
                players={players
                  .filter((p): p is NonNullable<typeof p> => p != null)
                  .map((p) => ({
                    wallet: p.wallet,
                    username: p.username,
                  }))}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Match list view
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 p-4">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl sm:text-3xl font-bold text-white">Live Matches</h1>
          <p className="text-white/50">Watch ongoing matches with a 5-second delay</p>
        </div>

        {/* Connection status */}
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-sm text-white/50">{connected ? 'Connected' : 'Connecting...'}</span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neon-500 border-t-transparent" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-center text-red-300">
            {error}
          </div>
        )}

        {/* No matches */}
        {!loading && !error && liveMatches.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
            <div className="mb-4 text-4xl">🎮</div>
            <h2 className="mb-2 text-xl font-semibold text-white">No Live Matches</h2>
            <p className="text-white/50">Check back later or start a match yourself!</p>
          </div>
        )}

        {/* Match list */}
        <AnimatePresence>
          <div className="space-y-4">
            {liveMatches.map((match) => (
              <motion.div
                key={match.matchId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="rounded-xl border border-white/10 bg-white/5 p-4 hover:border-neon-500/30 transition"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {/* Players */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                      <span className="text-sm font-medium text-white">
                        {match.players[0]?.username || 'Player 1'}
                      </span>
                      <span className="text-white/30">vs</span>
                      <span className="text-sm font-medium text-white">
                        {match.players[1]?.username || 'Player 2'}
                      </span>
                    </div>
                  </div>

                  {/* Match info */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-white/50">
                      Turn {match.currentTurn}/{8}
                    </span>
                    <span className="rounded-full bg-neon-500/20 px-3 py-1 text-xs text-neon-300">
                      {match.wagerAmount > 0
                        ? formatLamports(
                            BigInt(match.wagerAmount),
                            match.currency === 'USDC' ? 'USDC' : 'SOL',
                          )
                        : 'Practice'}
                    </span>
                    <button
                      onClick={() => handleWatch(match.matchId)}
                      disabled={!connected}
                      className="rounded-lg bg-neon-500/20 px-4 py-2 text-sm font-medium text-neon-300 hover:bg-neon-500/30 disabled:opacity-50 transition"
                    >
                      Watch
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>

        {/* Refresh button */}
        <div className="mt-8 text-center">
          <button
            onClick={fetchMatches}
            className="rounded-lg bg-white/10 px-6 py-2 text-sm text-white/70 hover:bg-white/20 transition"
          >
            Refresh Matches
          </button>
        </div>
      </div>
    </div>
  );
};
